<template>
  <Card class="elevation-2 pa-4 containerCards" :loading="processing">
    <div class="cardTitle">{{ $t('Профиль организации') }}</div>
    <v-form v-model="valid" ref="form" :readonly="!editing">
      <Input
        label="Наименование организации"
        placeholder="Введите наименование организации"
        v-model="contragent.fullName"
        :rules="rules.fullName"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        :required="editing"
      />
      <Input
        ref="contragentInnRef"
        label="CRN"
        placeholder="Введите CRN"
        v-model="contragent.inn"
        v-mask="masks.inn"
        :rules="rules.crn"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        :required="editing"
      />
      <Input
        label="Телефон"
        placeholder="Введите номер телефона"
        v-model="contragent.phone"
        :rules="rules.phone"
        :disabled="processing || !isOrgOrSupAdmin"
        :class="{ 'disabled': !(editing && isOrgOrSupAdmin) }"
      />
      <Input
        label="Адрес электронной почты"
        placeholder="Введите E-mail"
        v-model="contragent.email"
        :rules="rules.email"
        :disabled="processing || !isOrgOrSupAdmin"
        :class="{ 'disabled': !(editing && isOrgOrSupAdmin) }"
      />
      <div v-if="isSupplierContragent && categoriesList.total > 0">
        <CategoriesSelect
          v-if="editing"
          :card-title="$t('Категории деятельности')"
          :showSubTitle="false"
          :categories="categoriesList"
          :categories-selected="contragent.categories"
          @update:categories-selected-list="contragent.categories = $event"
        />
        <CategoriesView
          v-else
          :card-title="$t('Категории деятельности')"
          :categories="contragent.categories"
        />
      </div>
    </v-form>
    <v-card-actions>
      <div>
        <v-btn
          v-if="!editing"
          color="primary"
          density="comfortable"
          variant="outlined"
          :disabled="processing"
          @click="toPreviosPage()"
        >{{ $t('Назад') }}</v-btn>
        <v-btn
          v-if="editing"
          color="primary"
          density="comfortable"
          variant="outlined"
          :disabled="processing"
          @click="cancelAction()"
        >{{ $t('Отмена') }}</v-btn>
      </div>
      <div
        class="ml-auto"
        style="display: flex; gap: 10px;"
        v-if="isOrgOrSupAdmin"
      >
        <v-btn
          v-if="!editing && canToEdit"
          color="primary"
          density="comfortable"
          variant="elevated"
          :disabled="processing"
          @click="editAction()"
        >{{ $t('Редактировать') }}</v-btn>
        <v-btn
          v-if="editing"
          color="primary"
          density="comfortable"
          variant="elevated"
          @click="saveAction()"
          :disabled="processing || !valid"
        >{{ $t('Сохранить') }}</v-btn>
      </div>
    </v-card-actions>
  </Card>
</template>

<script>
  import api from '@/api';
  import { toast } from '@/lib/toast';
  import Config from "@/config";
  import { emitter } from "@/app";
  import Card from "@intec/vue-components/form/Card.vue";
  import Input from "@intec/vue-components/form/Input.vue";
  import { useEditContragent } from "@/lib/composable";
  import { isORGAdmin, isSUPAdmin } from '@/lib/user';
  import CategoriesSelect from '@/components/categories/CategoriesSelect.vue';
  import CategoriesView from '@/components/categories/CategoriesView.vue';
  import { useDialogStore } from '@/stores/dialog';

  export default {
    name: 'ProfileContragent',
    components: { Card, Input, CategoriesSelect, CategoriesView },
    props: {
      contragentId: {
        type: String,
      }
    },
    setup() {
      const { contragent, rules, masks, fillContragentData, contragentInnRef, SUPPLIER } = useEditContragent();
      const dialogStore = useDialogStore();
      return { contragent, rules, masks, fillContragentData, contragentInnRef, SUPPLIER, dialogStore }
    },
    computed: {
      canToEdit() {
        return Config.getUser().opts?.contragent?.id === this.contragentIdFromRequest;
      },
      isSupplierContragent() {
        return this.contragent?.isSupplier;
      },
      isOrgOrSupAdmin() {
        return this.libUser.isORGAdmin(this.currentUser?.role?.code)
            || this.libUser.isSUPAdmin(this.currentUser?.role?.code);
      }
    },
    data() {
      return {
        emitter,
        valid: true,
        editing: false,
        processing: false,
        currentUser: Config.getUser().opts,
        contragentIdFromRequest: null,
        libUser: {
          isORGAdmin: isORGAdmin,
          isSUPAdmin: isSUPAdmin
        },
        contragentMemory: {},
        categoriesList: [],
        contragentCategoriesListMem: [],
      };
    },
    methods: {
      async checkAllows() {
        let contragentId = await api.post(`/profile/${Config.getUser().opts.id}/getContragent`);
        if (contragentId.toString() == this.contragentId || Config.getUser().opts.roles.includes('ROLE_ADMIN')) {
          this.accessAllowed = true;
          this.editAccessAllowed = true;
        } else {
          let profileRole = await api.post(`/profile/${this.contragentId}/getContragentRole`);
          if (Config.getUser().opts.roles.includes('ROLE_CUSTOMER')
            || Config.getUser().opts.roles.includes('ROLE_SUPPLIER') && profileRole) {
            this.accessAllowed = true;
            this.editAccessAllowed = false;
          }
        }

        await Promise.all([
          this.loadCategories(),
          this.loadInfo()
        ]);
      },
      async load() {
        this.processing = true;
        try {
          const url = this.contragentId
              ? '/profile/contragent/' + this.contragentId + '/load'
              : '/profile/contragent/load';
          const contragent = await api.get(url);
          this.fillContragentData(contragent);
          this.contragentIdFromRequest = contragent?.id;
        } catch (e) {
          toast.error(e.message);
        }
        this.processing = false;
      },

      async loadCategories() {
        if (!this.contragent.type === this.SUPPLIER) return;

        try {
          const categories = await api.get('/vocab/categories');
          this.categoriesList = categories;
        } catch (e) {
          toast.error('Не удалось загрузить категории деятельности');
        }
      },
      async save() {
        const validator = await this.$refs.form.validate()
        if (!validator.valid) {
          return false;
        }
        let formData = new FormData();
        formData.append('data', JSON.stringify(this.fillContragentData()));
        await api.post(`/profile/contragent/${this.contragentId}/save`, formData);
        return true;
      },
      async saveAction() {
        if (this.contragentId) {
          toast.error('Запрещено сохранение чужого профиля');
          return;
        }

        this.processing = true;
        if ((await this.$refs.form.validate()).valid) {
          try {
            let formData = new FormData();
            formData.append('data', JSON.stringify(this.fillContragentData()));
            await api.post(`/profile/contragent/save`, formData);
            await Config.reload();
            this.emitter.emit('profileUpdate');
            this.editing = false;
          } catch (e) {
            toast.error(e.message);
          }
        } else {
          toast.error('Проверьте правильность заполнения формы');
        }
        this.processing = false;
      },
      editAction() {
        this.editing = true;
        this.contragentMemory = {...this.fillContragentData()};
      },
      cancelAction() {
        this.editing = false;
        this.fillContragentData(this.contragentMemory);
        this.contragent.categories = this.contragentCategoriesListMem;
        this.$refs.form.resetValidation();
      },
      toPreviosPage() {
        const returnRoute = this.$route.query.backRoute;
        if (returnRoute) {
          this.$router.push(returnRoute);
        } else {
          history.back();
        }
      },
    },
    async created() {
      await Promise.all([
        this.loadCategories(),
        this.load()
      ]);
    }
  };
</script>
